/**
 * Отображение soft-deleted аккаунта: бэк отдаёт `isDeleted` в авторах постов/
 * комментов, участниках, профиле, чате. Имя/фамилия в БД не меняются —
 * подмена только здесь, по флагу.
 */
import { DELETED_USER_NAME } from "#shared/utils/user";

export { DELETED_USER_NAME };
/** Cookie-метка «только что удалил аккаунт» — пропуск на /account-deleted. */
export const ACCOUNT_DELETED_FLAG_COOKIE = "account_deleted";
export const DELETED_USER_AVATAR = "/images/deleted-account.png";

type UserLike =
  | {
      firstName?: string | null;
      lastName?: string | null;
      nickname?: string | null;
      isDeleted?: boolean | null;
    }
  | null
  | undefined;

export const isDeletedUser = (user: UserLike): boolean => !!user?.isDeleted;

/** «Имя Фамилия» либо «Удалённый аккаунт». */
export const userDisplayName = (user: UserLike, fallback = ""): string => {
  if (!user) return fallback;
  if (user.isDeleted) return DELETED_USER_NAME;
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return name || user.nickname || fallback;
};

/** Аватар с учётом удаления: у удалённого всегда «гость». */
export const userAvatarSrc = (
  user: UserLike,
  src?: string | null,
): string | undefined => {
  if (user?.isDeleted) return DELETED_USER_AVATAR;
  return src ?? undefined;
};

/** Сколько полных дней осталось до даты (не меньше 0). */
export const daysUntil = (date: string | Date): number => {
  const ms = new Date(date).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
};

/** Сколько полных дней прошло с даты (не меньше 0). */
export const daysSince = (date: string | Date): number => {
  const ms = Date.now() - new Date(date).getTime();
  return Math.max(0, Math.floor(ms / (24 * 60 * 60 * 1000)));
};

/**
 * Payload ошибки бэка из $fetch-исключения BFF: ofetch кладёт H3-тело в
 * `error.data`, а BFF проксирует тело бэка в его поле `data` — поэтому
 * `{code, details}` живут либо в `error.data.data`, либо в `error.data`.
 */
export const backendErrorPayload = (
  error: unknown,
): { code?: string; details?: Record<string, unknown> } | undefined => {
  if (typeof error !== "object" || error === null) return undefined;
  const body = (error as { data?: { data?: unknown; code?: string } }).data;
  if (!body) return undefined;
  const inner = body.data && typeof body.data === "object" ? body.data : body;
  return inner as { code?: string; details?: Record<string, unknown> };
};
